import { useLazyQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  TextField,
  useTheme,
} from "@mui/material";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Page from "../../components/Page/Page";
import Text from "../../components/Styled/Text";
import { Attachment } from '../../graphql/graphql';
import { CHAT_BETWEEN_USERS, USER_CHATS } from '../../graphql/chat-messages-gql';
import MessageAttachment from './MessageAttachment';
import { messageCaption } from './messageCaption';

interface Member {
  _id: string;
  username?: string;
  profilePhoto?: { url?: string; thumbnailUrl?: string } | null;
  photos?: Array<{ url?: string; thumbnailUrl?: string }> | null;
}

interface ChatMessage {
  _id: string;
  fromId: string;
  text: string;
  created: string;
  attachments?: Array<Attachment | null> | null;
}

interface ChatNode {
  _id: string;
  updatedDate: string;
  members: Member[];
  lastMessage?: { _id: string; text: string; created: string; fromId: string } | null;
}

interface ChatWithMessages {
  _id: string;
  members: Member[];
  messages: { nodes: ChatMessage[] };
}

const ResultsArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 16px;
`;

const Pane = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  overflow: hidden;
`;

const ChatList = styled(Pane)`
  width: 340px;
  flex-shrink: 0;
`;

const PaneHeader = styled.div`
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

const PaneScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
`;

const ChatPane = styled(Pane)`
  flex: 1;
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
  padding: 14px 20px;
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

const MessagesContainer = styled(Box)`
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  gap: 2px;
  width: 100%;
  max-width: 860px;
  margin: 0 auto;
`;

const MessageRow = styled(Box)<{ $alignRight: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => (props.$alignRight ? "flex-end" : "flex-start")};
  align-items: flex-end;
  gap: 8px;
  width: 100%;
`;

const AvatarSlot = styled(Box)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const BubbleColumn = styled(Box)<{ $alignRight: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$alignRight ? "flex-end" : "flex-start")};
  max-width: 65%;
  min-width: 0;
`;

const MessageBubble = styled.div<{ $alignRight: boolean; $grouped: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 8px 14px;
  border-radius: 18px;
  background-color: ${(props) =>
    props.$alignRight ? props.theme.colors.primary : props.theme.surfaceRaised};
  color: ${(props) => (props.$alignRight ? "#ffffff" : props.theme.textColor)};
  border: 1px solid
    ${(props) => (props.$alignRight ? "transparent" : props.theme.border)};
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.4;
  border-bottom-right-radius: ${(props) =>
    props.$alignRight && !props.$grouped ? "6px" : "18px"};
  border-bottom-left-radius: ${(props) =>
    !props.$alignRight && !props.$grouped ? "6px" : "18px"};
`;

const DateSeparator = styled(Box)`
  display: flex;
  justify-content: center;
  margin: 12px 0 4px;
`;

const DateChip = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 999px;
  color: ${(props) => props.theme.secondaryTextColor};
  background: ${(props) => props.theme.surfaceHover};
`;

const MemberLink = styled(Link)`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

type SearchMode = "none" | "between" | "list";

const memberAvatarUrl = (m: Member | null | undefined): string | undefined => {
  return (
    m?.profilePhoto?.thumbnailUrl ||
    m?.profilePhoto?.url ||
    m?.photos?.[0]?.thumbnailUrl ||
    m?.photos?.[0]?.url ||
    undefined
  );
};

const ChatMessages: React.FC = () => {
  const theme = useTheme();
  const [userId1, setUserId1] = useState("");
  const [userId2, setUserId2] = useState("");
  const [searchMode, setSearchMode] = useState<SearchMode>("none");
  const [listSearchUserId, setListSearchUserId] = useState("");
  const [chat, setChat] = useState<ChatWithMessages | null>(null);
  const [userChats, setUserChats] = useState<ChatNode[]>([]);

  const [runChatBetween, chatBetweenState] = useLazyQuery(CHAT_BETWEEN_USERS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setChat(data?.chatBetweenUsers ?? null);
    },
  });
  const [runUserChats, userChatsState] = useLazyQuery(USER_CHATS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setUserChats(data?.userChats?.nodes ?? []);
    },
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const trimmed1 = userId1.trim();
  const trimmed2 = userId2.trim();

  const handleFind = () => {
    if (trimmed1 && trimmed2) {
      setSearchMode("between");
      setUserChats([]);
      setChat(null);
      runChatBetween({ variables: { userId1: trimmed1, userId2: trimmed2 } });
    } else if (trimmed1 || trimmed2) {
      const onlyId = trimmed1 || trimmed2;
      setSearchMode("list");
      setListSearchUserId(onlyId);
      setChat(null);
      runUserChats({ variables: { userId: onlyId } });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleFind();
    }
  };

  const handleSelectChat = (clickedChat: ChatNode) => {
    if (clickedChat.members.length < 2) return;
    const [a, b] = clickedChat.members;
    setUserId1(a._id);
    setUserId2(b._id);
    setChat(null);
    runChatBetween({ variables: { userId1: a._id, userId2: b._id } });
  };

  const orderedMessages = useMemo(() => {
    if (!chat) return [] as ChatMessage[];
    return [...chat.messages.nodes].sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    );
  }, [chat]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [chat?._id, orderedMessages.length]);

  const memberById = useMemo(() => {
    const map = new Map<string, Member>();
    chat?.members.forEach((m) => map.set(m._id, m));
    return map;
  }, [chat]);

  const loading = chatBetweenState.loading || userChatsState.loading;
  const error = chatBetweenState.error || userChatsState.error;

  const showingChat = searchMode !== "none" && !!chat;
  const showingUserChats =
    searchMode === "list" && userChatsState.called && !userChatsState.loading;

  const oneIdMode = (!!trimmed1 && !trimmed2) || (!trimmed1 && !!trimmed2);

  return (
    <Page
      fill
      title="Chat messages"
      description="Enter two user IDs to view their chat, or one user ID to list all their chats."
      toolbar={
        <>
          <TextField
            label="User ID 1"
            value={userId1}
            onChange={(e) => setUserId1(e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ minWidth: 280 }}
          />
          <TextField
            label="User ID 2"
            value={userId2}
            onChange={(e) => setUserId2(e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
            sx={{ minWidth: 280 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleFind}
            disabled={(!trimmed1 && !trimmed2) || loading}
            sx={{ height: 40 }}
          >
            Find
          </Button>
          {loading && <CircularProgress size={20} />}
        </>
      }
    >
      {error && (
        <ErrorAlert error={error.message || "Failed to load chat data."} />
      )}

      <ResultsArea>
        {showingUserChats && (
          <ChatList>
            <PaneHeader>
              <Text size={14} weight={600}>
                {userChats.length} chat{userChats.length === 1 ? "" : "s"}
              </Text>
            </PaneHeader>
            <PaneScroll>
            {userChats.length === 0 ? (
              <Box sx={{ p: 1 }}>
                <Text size={13} color={theme.secondaryTextColor}>
                  No chats found for this user.
                </Text>
              </Box>
            ) : (
              <List dense disablePadding>
                {userChats.map((c) => {
                  const other =
                    c.members.find((m) => m._id !== listSearchUserId) ||
                    c.members[0];
                  const isSelected = chat?._id === c._id;
                  return (
                    <ListItem
                      key={c._id}
                      component="div"
                      onClick={() => handleSelectChat(c)}
                      sx={{
                        cursor: "pointer",
                        borderRadius: 1,
                        mb: 0.5,
                        backgroundColor: isSelected
                          ? theme.surfaceActive
                          : "transparent",
                        "&:hover": {
                          backgroundColor: isSelected
                            ? theme.surfaceActive
                            : theme.surfaceHover,
                        },
                      }}
                    >
                      <Avatar
                        src={memberAvatarUrl(other)}
                        sx={{ mr: 1.5, width: 36, height: 36 }}
                      >
                        {(other?.username || "U").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Text size={14} weight={500}>
                          {other?.username || "Unknown"}
                        </Text>
                        <Text
                          size={12}
                          color={theme.mutedTextColor}
                          style={{
                            display: "block",
                            fontFamily: theme.monoFontFamily,
                          }}
                        >
                          {other?._id}
                        </Text>
                        <Text
                          size={12}
                          color={theme.secondaryTextColor}
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            marginTop: 2,
                          }}
                        >
                          {c.lastMessage?.text || "No messages"}
                        </Text>
                        <Text size={11} color={theme.mutedTextColor}>
                          {format(new Date(c.updatedDate), "MMM d, h:mm a")}
                        </Text>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
            </PaneScroll>
          </ChatList>
        )}

        {showingChat && (
          <ChatPane>
            <ChatHeader>
              {(() => {
                const leftMember = memberById.get(trimmed1) || null;
                const rightMember = memberById.get(trimmed2) || null;
                const renderHeader = (
                  m: Member | null,
                  fallbackId: string,
                  align: "left" | "right",
                  accentColor: string,
                ) => (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexDirection: align === "right" ? "row-reverse" : "row",
                      textAlign: align,
                    }}
                  >
                    <Avatar
                      src={memberAvatarUrl(m)}
                      sx={{
                        width: 44,
                        height: 44,
                        border: `2px solid ${accentColor}`,
                      }}
                    >
                      {(m?.username || "U").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <MemberLink to={`/user/${m?._id || fallbackId}`}>
                        <Text size={14} weight={600}>
                          {m?.username || "Unknown"}
                        </Text>
                      </MemberLink>
                      <Text
                        size={11}
                        color={theme.mutedTextColor}
                        style={{
                          display: "block",
                          fontFamily: theme.monoFontFamily,
                        }}
                      >
                        {m?._id || fallbackId}
                      </Text>
                      <Text size={11} color={accentColor}>
                        {align === "left" ? "left side" : "right side"}
                      </Text>
                    </Box>
                  </Box>
                );

                return (
                  <>
                    {renderHeader(
                      leftMember,
                      trimmed1,
                      "left",
                      theme.secondaryTextColor,
                    )}
                    {renderHeader(
                      rightMember,
                      trimmed2,
                      "right",
                      theme.colors.primary,
                    )}
                  </>
                );
              })()}
            </ChatHeader>

            <MessagesContainer ref={messagesContainerRef}>
              {orderedMessages.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Text size={13} color={theme.secondaryTextColor}>
                    No messages in this chat.
                  </Text>
                </Box>
              ) : (
                orderedMessages.map((msg, idx) => {
                  const attachments = (msg.attachments ?? []).filter(
                    (attachment): attachment is Attachment => attachment !== null
                  );
                  const caption = messageCaption(msg.text, attachments);
                  const prev = orderedMessages[idx - 1];
                  const next = orderedMessages[idx + 1];
                  const showDateSeparator =
                    !prev ||
                    new Date(prev.created).toDateString() !==
                      new Date(msg.created).toDateString();

                  const alignRight = msg.fromId === trimmed2;
                  const sender = memberById.get(msg.fromId);

                  const isFirstInGroup =
                    !prev || prev.fromId !== msg.fromId || showDateSeparator;
                  const isLastInGroup = !next || next.fromId !== msg.fromId;
                  const grouped = !isLastInGroup;

                  return (
                    <Box
                      key={msg._id}
                      sx={{ mt: isFirstInGroup ? 1.5 : 0.25 }}
                    >
                      {showDateSeparator && (
                        <DateSeparator>
                          <DateChip>
                            {format(new Date(msg.created), "MMM d, yyyy")}
                          </DateChip>
                        </DateSeparator>
                      )}
                      <MessageRow $alignRight={alignRight}>
                        {!alignRight && (
                          <AvatarSlot>
                            {isLastInGroup && (
                              <Avatar
                                src={memberAvatarUrl(sender)}
                                sx={{ width: 32, height: 32 }}
                              >
                                {(sender?.username || "U")
                                  .charAt(0)
                                  .toUpperCase()}
                              </Avatar>
                            )}
                          </AvatarSlot>
                        )}
                        <BubbleColumn $alignRight={alignRight}>
                          {isFirstInGroup && (
                            <Text
                              size={11}
                              color={theme.secondaryTextColor}
                              style={{
                                marginBottom: 4,
                                padding: "0 8px",
                              }}
                            >
                              {sender?.username || msg.fromId}
                            </Text>
                          )}
                          <MessageBubble
                            $alignRight={alignRight}
                            $grouped={grouped}
                            title={format(
                              new Date(msg.created),
                              "MMM d, yyyy h:mm:ss a",
                            )}
                          >
                            {attachments.map((attachment) => (
                              <MessageAttachment key={attachment.uploadId} attachment={attachment} />
                            ))}
                            {caption && <span>{caption}</span>}
                          </MessageBubble>
                          {isLastInGroup && (
                            <Text
                              size={10}
                              color={theme.mutedTextColor}
                              style={{
                                marginTop: 3,
                                padding: "0 8px",
                              }}
                            >
                              {format(new Date(msg.created), "h:mm a")}
                            </Text>
                          )}
                        </BubbleColumn>
                        {alignRight && (
                          <AvatarSlot>
                            {isLastInGroup && (
                              <Avatar
                                src={memberAvatarUrl(sender)}
                                sx={{ width: 32, height: 32 }}
                              >
                                {(sender?.username || "U")
                                  .charAt(0)
                                  .toUpperCase()}
                              </Avatar>
                            )}
                          </AvatarSlot>
                        )}
                      </MessageRow>
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>
          </ChatPane>
        )}

        {!showingChat &&
          !showingUserChats &&
          chatBetweenState.called &&
          !chatBetweenState.loading && (
            <EmptyState
              style={{ flex: 1 }}
              Icon={ChatBubbleLeftRightIcon}
              title="No chat found"
              description="No chat exists between these two users."
            />
          )}

        {!chatBetweenState.called &&
          !userChatsState.called &&
          !loading && (
            <EmptyState
              style={{ flex: 1 }}
              Icon={ChatBubbleLeftRightIcon}
              title="Look up a conversation"
              description={
                oneIdMode
                  ? "Press Find to list all chats for this user."
                  : "Enter user IDs and press Find."
              }
            />
          )}
      </ResultsArea>
    </Page>
  );
};

export default ChatMessages;
